import type { TOC } from '@ember/component/template-only';

// ---------------------------------------------------------------------------
// Card – minimal wrapper for the standard reimagined card treatment
// ---------------------------------------------------------------------------

/**
 * Padding size options for the card. `xs` and `xl` exist because the surfaces
 * this component is meant to replace use them: a metric tile is p-3, a
 * full-width empty state is p-8. Without them those surfaces stay hand-rolled.
 */
export type CardPadding = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Radius steps a card can take, from DESIGN.md's radius table. `nested` is the
 * 12px step used by a card inside another card — a metric tile in a panel, a
 * sub-panel in a hero — where the outer surface keeps the 16px `card` step so
 * the nesting reads as hierarchy.
 */
export type CardRadius = 'compact' | 'nested' | 'card';

/**
 * How far the card sits off the surface behind it. `none` is the flat variant —
 * a hairline and no shadow — which is what one of many peers inside an already
 * elevated panel takes, so the panel is the only thing that reads as lifted
 * (DESIGN.md § Elevation).
 */
export type CardElevation = 'none' | 'card' | 'raised';

interface CardSignature {
  Args: {
    /** Padding size (default: 'md' = p-5) */
    padding?: CardPadding;
    /**
     * Radius step (default: 'card' = rounded-2xl, 16px). 'nested' is 12px for a
     * card inside a card; 'compact' is 8px for dense list rows.
     */
    radius?: CardRadius;
    /**
     * Elevation step (default: 'card'). Use 'none' for a row or tile that is one
     * of many peers inside a panel, and 'raised' for a surface deliberately
     * lifted above its neighbours.
     */
    elevation?: CardElevation;
    /**
     * Deprecated alias for `@radius="compact"`. Kept so existing call sites keep
     * rendering; prefer `@radius`, which can also express the nested step.
     */
    compact?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

const paddingClasses: Record<CardPadding, string> = {
  none: '',
  xs: 'p-3',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
  xl: 'p-8',
};

const radiusClasses: Record<CardRadius, string> = {
  compact: 'rounded-lg',
  nested: 'rounded-xl',
  card: 'rounded-2xl',
};

const elevationClasses: Record<CardElevation, string> = {
  none: '',
  card: 'tio-elevation-card',
  raised: 'tio-elevation-card-raised',
};

const getPadding = (padding: CardPadding = 'md') => paddingClasses[padding];

const getElevation = (elevation: CardElevation = 'card') => elevationClasses[elevation];

// `@compact` predates `@radius` and means the 8px step.
const getRadius = (radius: CardRadius | undefined, compact: boolean | undefined) =>
  radiusClasses[radius ?? (compact ? 'compact' : 'card')];

/**
 * Card — the standard card surface: surface fill, hairline border, card radius
 * and card elevation.
 *
 * Reach for this instead of hand-writing `tio-bg-surface rounded-* border
 * tio-border-default shadow-* p-X`. It carries the treatment the Tuition
 * Assistance dashboard ships, which is what every page standardizes on
 * (DESIGN.md § Elevation), so a change to that standard lands here rather than
 * across every call site.
 *
 * @example Basic usage
 * ```gts
 * import Card from 'tio-ui/components/card';
 *
 * <Card>
 *   <p>Card content</p>
 * </Card>
 * ```
 *
 * @example With large padding
 * ```gts
 * <Card @padding="lg">
 *   <p>Card content with more padding</p>
 * </Card>
 * ```
 *
 * @example A metric tile inside another card — 12px so the nesting reads as
 * hierarchy against the outer 16px surface
 * ```gts
 * <Card @radius="nested" @padding="xs">
 *   <p>4 active requests</p>
 * </Card>
 * ```
 *
 * @example One of many rows inside a panel — hairline only, so the panel stays
 * the thing that reads as lifted
 * ```gts
 * <Card @elevation="none" @radius="compact" @padding="sm">
 *   <p>March contribution</p>
 * </Card>
 * ```
 */
const Card: TOC<CardSignature> = <template>
  <div
    data-test-card
    class="tio-bg-surface
      {{getRadius @radius @compact}}
      border tio-border-default
      {{getElevation @elevation}}
      {{getPadding @padding}}"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

export default Card;

export type { CardSignature };
